import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useReadiness } from '@/stores/readinessStore';
import { useQuery } from '@tanstack/react-query';
import { fetchMolds } from '@/lib/api/molds';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useMaintenanceKPIs } from '@/hooks/useMaintenanceKPIs';
import type { WorkOrder } from '@/types/maintenance';

interface Props {
  workOrders: WorkOrder[];
  compact?: boolean;
}

interface KPIConfig {
  key: string;
  label: string;
  subtitle: string;
  count: number | string;
  dotColor: string;
  filterPath: string;
}

/** Returns true if the timestamp falls within the current shift window */
function isInCurrentShift(dateStr: string, startHour: number, endHour: number): boolean {
  const d = new Date(dateStr);
  const now = new Date();

  const shiftStart = new Date(now);
  shiftStart.setHours(startHour, 0, 0, 0);

  const shiftEnd = new Date(now);
  shiftEnd.setHours(endHour, 0, 0, 0);

  if (endHour <= startHour) {
    // Overnight shift (e.g. 22:00–06:00)
    if (now.getHours() < endHour) {
      // Early morning portion — shift started yesterday
      shiftStart.setDate(shiftStart.getDate() - 1);
    } else {
      shiftEnd.setDate(shiftEnd.getDate() + 1);
    }
  }

  return d >= shiftStart && d <= shiftEnd;
}

export function SnapshotKPIs({ workOrders, compact = false }: Props) {
  const navigate = useNavigate();
  const { session, moldRows } = useReadiness();
  const { user } = useAuth();
  const { settings } = useOrgSettings();

  const { data: molds = [] } = useQuery({
    queryKey: ['molds', user?.organization_id],
    queryFn: () => fetchMolds(user?.organization_id ?? null),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // PM compliance — rolling 30 days
  const { pmBreakdown } = useMaintenanceKPIs({
    period: 'month',
    orgId: user?.organization_id ?? null,
  });

  const inProgressCount = workOrders.filter(wo => wo.status === 'in_progress').length;
  const requestedCount = workOrders.filter(wo => wo.status === 'requested').length;

  const closedThisShift = workOrders.filter(
    wo =>
      wo.status === 'closed' &&
      wo.end_time &&
      isInCurrentShift(wo.end_time, settings.shift_start_hour, settings.shift_end_hour),
  );
  const closedTodayCount = closedThisShift.length;
  const verifiedTodayCount = closedThisShift.filter(wo => wo.tested_validated === true).length;
  const unverifiedCount = closedTodayCount - verifiedTodayCount;

  const overduePMMolds = molds.filter(m => m.pm_due_status === 'red');
  const overduePMCount = overduePMMolds.length;

  const worstShotsOverdue = overduePMMolds.reduce((max, m) => {
    if (m.shots_until_pm == null) return max;
    return Math.max(max, Math.abs(m.shots_until_pm));
  }, 0);

  const pmDueSoonCount = molds.filter(m => m.pm_due_status === 'amber').length;

  // PM compliance card values
  const compliance = pmBreakdown.compliance;
  const pmComplianceDotColor =
    compliance == null
      ? 'bg-muted-foreground/40'
      : compliance >= 90
      ? 'bg-[hsl(var(--status-green))]'
      : compliance >= 70
      ? 'bg-[hsl(var(--status-amber))]'
      : 'bg-[hsl(var(--status-red))]';

  // "Closed This Shift" dot color: amber if unverified, green if all verified
  const closedDotColor =
    closedTodayCount === 0
      ? 'bg-muted-foreground/40'
      : unverifiedCount > 0
      ? 'bg-[hsl(var(--status-amber))]'
      : 'bg-[hsl(var(--status-green))]';

  const closedSubtitle =
    closedTodayCount === 0
      ? 'Completed this shift'
      : unverifiedCount > 0
      ? `${unverifiedCount} unverified`
      : `${verifiedTodayCount} verified`;

  const cards: KPIConfig[] = [
    {
      key: 'in_maintenance',
      label: 'In Maintenance',
      subtitle: 'Being worked by team',
      count: inProgressCount,
      dotColor: inProgressCount > 0 ? 'bg-molten' : 'bg-muted-foreground/40',
      filterPath: '/maintenance/work-orders?filters=in_progress',
    },
    {
      key: 'requested',
      label: 'Ready to Start',
      subtitle: 'Waiting to start',
      count: requestedCount,
      dotColor: 'bg-muted-foreground/40',
      filterPath: '/maintenance/work-orders?filters=requested',
    },
    {
      key: 'closed_today',
      label: 'Closed This Shift',
      subtitle: closedSubtitle,
      count: closedTodayCount,
      dotColor: closedDotColor,
      filterPath: '/maintenance/work-orders?filters=closed_today&tab=closed',
    },
    {
      key: 'pm_due_soon',
      label: 'PM Due Soon',
      subtitle: 'Approaching PM threshold',
      count: pmDueSoonCount,
      dotColor: pmDueSoonCount > 0 ? 'bg-[hsl(var(--status-amber))]' : 'bg-muted-foreground/40',
      filterPath: '/maintenance/molds?status=due_soon',
    },
    {
      key: 'overdue_pm',
      label: 'PM Overdue',
      subtitle: worstShotsOverdue > 0
        ? `Worst: ${worstShotsOverdue.toLocaleString()} shots over`
        : 'Past maintenance threshold',
      count: overduePMCount,
      dotColor: overduePMCount > 0 ? 'bg-[hsl(var(--status-red))]' : 'bg-muted-foreground/40',
      filterPath: '/maintenance/molds?status=overdue',
    },
    {
      key: 'pm_compliance',
      label: 'PM Compliance',
      subtitle:
        compliance != null
          ? `${pmBreakdown.completed}/${pmBreakdown.total} on time (30d)`
          : 'No PM data',
      count: compliance != null ? `${compliance}%` : '—',
      dotColor: pmComplianceDotColor,
      filterPath: '/maintenance/reports',
    },
  ];

  return (
    <div className={compact ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'}>
      {cards.map(card => (
        <button
          key={card.key}
          onClick={() => navigate(card.filterPath)}
          className={cn(
            'group bg-card rounded-lg border border-border/50 text-left',
            compact ? 'px-3 py-3' : 'px-5 py-4',
            'cursor-pointer transition-all hover:shadow-sm hover:border-border active:scale-[0.98]',
          )}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className={cn('h-2 w-2 rounded-full shrink-0', card.dotColor)} />
            <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{card.label}</span>
          </div>
          <div className={cn('font-mono font-semibold tracking-tight leading-none', compact ? 'text-xl' : 'text-2xl')}>{card.count}</div>
          <div className="text-[11px] text-muted-foreground/60 mt-1.5 flex items-center gap-1">
            {card.subtitle}
            <ArrowRight className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100" />
          </div>
        </button>
      ))}
    </div>
  );
}
